import { Box, Button, Dialog, Divider, IconButton, Stack, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import Iconify from '@/components/iconify';
import { PATH_DASHBOARD } from '@/routes/paths';
import {
  CAMPAIGN_AWARDS_PATH,
  CAMPAIGN_PHASE,
  fillCampaignText,
  formatCampaignMoney,
} from '@/utils/blackNovemberCampaign';
import BlackNovemberAward from './BlackNovemberAward';
import BlackNovemberMilestones from './BlackNovemberMilestones';
import { BLACK_NOVEMBER_COPY } from './blackNovemberCopy';
import { BLACK_CAKTO } from './blackNovemberTheme';

const SectionTitle = ({ children }) => (
  <Typography
    sx={{
      fontSize: 10.5,
      letterSpacing: '0.14em',
      textTransform: 'uppercase',
      color: BLACK_CAKTO.faint,
      mb: 1.25,
    }}
  >
    {children}
  </Typography>
);

SectionTitle.propTypes = { children: PropTypes.node };

const Body = ({ children }) => (
  <Typography sx={{ fontSize: 14, fontWeight: 300, color: BLACK_CAKTO.body, lineHeight: 1.6 }}>
    {children}
  </Typography>
);

Body.propTypes = { children: PropTypes.node };

const Figure = ({ label, value }) => (
  <Box sx={{ minWidth: 96 }}>
    <Typography
      sx={{
        fontSize: 10.5,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: BLACK_CAKTO.faint,
        mb: 0.5,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: 18,
        fontWeight: 300,
        letterSpacing: '-0.02em',
        color: BLACK_CAKTO.ink,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value}
    </Typography>
  </Box>
);

Figure.propTypes = { label: PropTypes.string, value: PropTypes.string };

/** O título em dois tons do site: a primeira linha clara, a segunda apagada. */
const HeroTitle = ({ title, muted }) => (
  <Typography
    component="h2"
    sx={{
      fontSize: { xs: 28, sm: 34 },
      fontWeight: 400,
      lineHeight: 1.05,
      letterSpacing: '-0.045em',
      color: BLACK_CAKTO.ink,
    }}
  >
    {title}
    <Box component="span" sx={{ display: 'block', color: BLACK_CAKTO.muted }}>
      {muted}
    </Box>
  </Typography>
);

HeroTitle.propTypes = { title: PropTypes.string, muted: PropTypes.string };

const primarySx = {
  borderRadius: 99,
  px: 2.25,
  textTransform: 'none',
  fontWeight: 500,
  boxShadow: 'none',
  color: BLACK_CAKTO.onLight,
  bgcolor: BLACK_CAKTO.ink,
  '&:hover': { bgcolor: '#DCE3DE', boxShadow: 'none' },
};

const secondarySx = {
  borderRadius: 99,
  px: 2.25,
  textTransform: 'none',
  fontWeight: 400,
  color: BLACK_CAKTO.ink,
  borderColor: BLACK_CAKTO.line,
  '&:hover': { borderColor: BLACK_CAKTO.lineStrong, bgcolor: 'rgba(198,232,209,.06)' },
};

const divider = <Divider sx={{ borderColor: BLACK_CAKTO.line }} />;

/**
 * O material que a faixa abre, no visual da seção do troféu do site. Tem três
 * versões — campanha em pé, troféu conquistado e novembro fechado sem o troféu
 * — porque o produtor chega aqui com três perguntas diferentes: como conquistar,
 * o que acontece agora, e quanto eu fiz.
 */
export default function BlackNovemberDialog({
  open,
  onClose,
  progress,
  awardsPath = CAMPAIGN_AWARDS_PATH,
}) {
  const navigate = useNavigate();
  const copy = BLACK_NOVEMBER_COPY.dialog;
  const { labels } = copy;

  const isWon = progress?.phase === CAMPAIGN_PHASE.RESULTS_WON;
  const isMissed = progress?.phase === CAMPAIGN_PHASE.RESULTS_MISSED;
  const isTeaser = progress?.phase === CAMPAIGN_PHASE.TEASER;
  // A meta e o ano vêm da API, então o dialog nunca mostra um valor diferente do
  // que a faixa mediu, e nenhuma edição fica escrita no texto.
  const goalLabel = formatCampaignMoney(progress?.goal);
  const fill = (text) =>
    fillCampaignText(text, {
      year: progress?.year,
      goal: progress?.goal,
      sales: progress?.sales,
    });

  const go = (path) => () => {
    onClose();
    navigate(path);
  };

  if (!progress) return null;

  const hero = (() => {
    if (isWon) {
      return {
        eyebrow: copy.won.eyebrow,
        title: copy.won.title,
        titleMuted: copy.won.titleMuted,
        lead: fill(copy.won.lead),
        celebrating: true,
      };
    }

    if (isMissed) {
      return {
        eyebrow: copy.missed.eyebrow,
        title: copy.missed.title,
        titleMuted: copy.missed.titleMuted,
        lead: fill(copy.missed.lead),
      };
    }

    return {
      eyebrow: isTeaser ? copy.running.teaserEyebrow : copy.running.eyebrow,
      title: copy.title,
      titleMuted: copy.titleMuted,
      lead: fill(copy.running.lead),
      celebrating: progress.achieved,
    };
  })();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#0B0F0C',
          backgroundImage: 'none',
          border: `1px solid ${BLACK_CAKTO.line}`,
          borderRadius: 2,
          // O material passa da altura de um notebook: rola dentro do dialog, e o
          // canto arredondado continua recortando o fundo do topo.
          overflowY: 'auto',
          fontFamily: BLACK_CAKTO.font,
          '& .MuiTypography-root': { fontFamily: 'inherit' },
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          textAlign: 'center',
          px: { xs: 2.5, sm: 3.5 },
          pt: 3.5,
          pb: 3,
          background: BLACK_CAKTO.rings,
        }}
      >
        <IconButton
          aria-label={copy.close}
          onClick={onClose}
          sx={{ position: 'absolute', top: 10, right: 10, color: BLACK_CAKTO.faint }}
        >
          <Iconify icon="eva:close-fill" width={20} />
        </IconButton>

        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            mb: 1.75,
            fontSize: 10.5,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            border: '1px solid',
            borderColor: hero.celebrating ? BLACK_CAKTO.lineStrong : BLACK_CAKTO.line,
            color: hero.celebrating ? BLACK_CAKTO.mint : BLACK_CAKTO.muted,
            borderRadius: 99,
            px: 1.25,
            py: '3px',
          }}
        >
          {hero.celebrating ? <Iconify icon="eva:checkmark-fill" width={12} /> : null}
          {hero.eyebrow}
        </Box>

        <HeroTitle title={hero.title} muted={hero.titleMuted} />

        <Typography
          sx={{
            mt: 1.5,
            mx: 'auto',
            maxWidth: 420,
            fontSize: 14.5,
            fontWeight: 300,
            lineHeight: 1.55,
            color: BLACK_CAKTO.body,
          }}
        >
          {hero.lead}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: -0.5 }}>
          <BlackNovemberAward
            large
            height={{ xs: 170, sm: 220 }}
            muted={isMissed}
            title={BLACK_NOVEMBER_COPY.award}
          />
        </Box>

        <Box
          sx={{
            display: 'inline-flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.5,
            px: 2.75,
            py: 1.25,
            borderRadius: 1.5,
            border: `1px solid ${BLACK_CAKTO.line}`,
            background: 'rgba(10,16,12,.75)',
          }}
        >
          <Typography
            sx={{ fontSize: 22, fontWeight: 300, letterSpacing: '-0.02em', color: BLACK_CAKTO.ink }}
          >
            {BLACK_NOVEMBER_COPY.periodChip.days}
            <Box
              component="span"
              sx={{
                ml: 1,
                fontSize: 10,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: BLACK_CAKTO.muted,
              }}
            >
              {BLACK_NOVEMBER_COPY.periodChip.month}
            </Box>
          </Typography>
          <Typography sx={{ fontSize: 11, color: BLACK_CAKTO.muted }}>
            {BLACK_NOVEMBER_COPY.periodChip.label}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ px: { xs: 2.5, sm: 3.5 }, py: 3 }}>
        {isWon ? (
          <Stack spacing={2.5} divider={divider}>
            <Box>
              <SectionTitle>{copy.won.askTitle}</SectionTitle>
              <Body>{copy.won.askText}</Body>
            </Box>

            <Box>
              <SectionTitle>{copy.won.stepsTitle}</SectionTitle>
              <Stack component="ol" spacing={1.25} sx={{ m: 0, pl: 2.5 }}>
                {copy.won.steps.map((step) => (
                  <Box
                    component="li"
                    key={step}
                    sx={{ color: BLACK_CAKTO.body, fontSize: 14, fontWeight: 300 }}
                  >
                    {step}
                  </Box>
                ))}
              </Stack>
            </Box>

            <Box>
              <SectionTitle>{copy.won.numbersTitle}</SectionTitle>
              <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                <Figure label={labels.approved} value={formatCampaignMoney(progress.sales)} />
                <Figure label={labels.goalRunning} value={goalLabel} />
                <Figure label={labels.period} value={BLACK_NOVEMBER_COPY.period} />
              </Stack>
            </Box>

            <Box>
              <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                <Button variant="contained" onClick={go(awardsPath)} sx={primarySx}>
                  {copy.won.primary}
                </Button>
                <Button
                  variant="outlined"
                  onClick={go(PATH_DASHBOARD.general.mySales)}
                  sx={secondarySx}
                >
                  {copy.won.secondary}
                </Button>
              </Stack>
              <Typography sx={{ fontSize: 12, color: BLACK_CAKTO.faint, lineHeight: 1.5 }}>
                {copy.won.fine}
              </Typography>
            </Box>
          </Stack>
        ) : null}

        {isMissed ? (
          <Stack spacing={2.5} divider={divider}>
            <Box>
              <SectionTitle>{copy.missed.numbersTitle}</SectionTitle>
              <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                <Figure label={labels.you} value={formatCampaignMoney(progress.sales)} />
                <Figure label={labels.goal} value={goalLabel} />
                <Figure label={labels.difference} value={formatCampaignMoney(progress.missing)} />
              </Stack>
              <Body>{copy.missed.closedText}</Body>
            </Box>

            <Box>
              <SectionTitle>{copy.missed.nextTitle}</SectionTitle>
              <Body>{copy.missed.nextText}</Body>
            </Box>

            <Box>
              <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                <Button variant="contained" onClick={go(awardsPath)} sx={primarySx}>
                  {copy.missed.primary}
                </Button>
                <Button
                  variant="outlined"
                  onClick={go(PATH_DASHBOARD.general.mySales)}
                  sx={secondarySx}
                >
                  {copy.missed.secondary}
                </Button>
              </Stack>
              <Typography sx={{ fontSize: 12, color: BLACK_CAKTO.faint, lineHeight: 1.5 }}>
                {copy.missed.fine}
              </Typography>
            </Box>
          </Stack>
        ) : null}

        {!isWon && !isMissed ? (
          <Stack spacing={2.5} divider={divider}>
            <Box>
              <Typography
                component="h3"
                sx={{
                  fontSize: 22,
                  fontWeight: 400,
                  letterSpacing: '-0.035em',
                  color: BLACK_CAKTO.ink,
                  mb: 1.75,
                }}
              >
                {copy.running.stepsTitle}{' '}
                <Box component="span" sx={{ color: BLACK_CAKTO.muted }}>
                  {copy.running.stepsTitleMuted}
                </Box>
              </Typography>

              <Stack component="ol" spacing={1.5} sx={{ m: 0, p: 0, listStyle: 'none' }}>
                {copy.running.steps.map((step, index) => (
                  <Stack component="li" key={step.title} direction="row" spacing={1.75}>
                    <Box
                      aria-hidden
                      sx={{
                        width: 28,
                        height: 28,
                        flexShrink: 0,
                        display: 'grid',
                        placeItems: 'center',
                        borderRadius: '50%',
                        border: `1px solid ${BLACK_CAKTO.line}`,
                        background: 'rgba(198,232,209,.04)',
                        fontSize: 12,
                        color: BLACK_CAKTO.muted,
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 14.5, color: BLACK_CAKTO.ink }}>
                        {step.title}
                      </Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 300, color: BLACK_CAKTO.body }}>
                        {fill(step.text)}
                      </Typography>
                    </Box>
                  </Stack>
                ))}
              </Stack>

              {isTeaser ? null : (
                <Box
                  sx={{
                    mt: 2.25,
                    p: 2,
                    borderRadius: 1.75,
                    border: `1px solid ${BLACK_CAKTO.line}`,
                    background: 'rgba(198,232,209,.03)',
                  }}
                >
                  <Typography sx={{ fontSize: 12, color: BLACK_CAKTO.muted }}>
                    {copy.running.revenueTitle}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 34,
                      fontWeight: 300,
                      letterSpacing: '-0.03em',
                      color: BLACK_CAKTO.ink,
                      fontVariantNumeric: 'tabular-nums',
                      my: 0.25,
                    }}
                  >
                    {formatCampaignMoney(progress.sales)}
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: BLACK_CAKTO.muted }}>
                    {progress.achieved ? (
                      <Box component="span" sx={{ color: BLACK_CAKTO.mint }}>
                        {copy.running.achieved}
                      </Box>
                    ) : (
                      <>
                        {BLACK_NOVEMBER_COPY.strip.missing}{' '}
                        <Box component="span" sx={{ color: BLACK_CAKTO.mint, fontWeight: 500 }}>
                          {formatCampaignMoney(progress.missing)}
                        </Box>{' '}
                        {copy.running.missingSuffix}
                      </>
                    )}
                  </Typography>
                  <BlackNovemberMilestones
                    percent={progress.percent}
                    goal={progress.goal}
                    label={BLACK_NOVEMBER_COPY.strip.progress}
                    showLabels
                    sx={{ mt: 2 }}
                  />
                </Box>
              )}
            </Box>

            <Box>
              <SectionTitle>{copy.running.rulesTitle}</SectionTitle>
              <Stack component="ul" spacing={1} sx={{ m: 0, pl: 2.5 }}>
                {copy.running.rules.map((rule) => (
                  <Box
                    component="li"
                    key={rule}
                    sx={{ color: BLACK_CAKTO.body, fontSize: 13.5, fontWeight: 300 }}
                  >
                    {rule}
                  </Box>
                ))}
              </Stack>
            </Box>

            <Box>
              <SectionTitle>{copy.running.afterTitle}</SectionTitle>
              <Body>{copy.running.afterText}</Body>
            </Box>

            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              <Button
                variant="contained"
                onClick={go(PATH_DASHBOARD.general.mySales)}
                sx={primarySx}
              >
                {copy.running.primary}
              </Button>
              <Button variant="outlined" onClick={go(awardsPath)} sx={secondarySx}>
                {copy.running.secondary}
              </Button>
            </Stack>
          </Stack>
        ) : null}
      </Box>
    </Dialog>
  );
}

BlackNovemberDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  progress: PropTypes.shape({
    phase: PropTypes.string,
    goal: PropTypes.number,
    sales: PropTypes.number,
    missing: PropTypes.number,
    percent: PropTypes.number,
    achieved: PropTypes.bool,
    // Usado por `fill` para preencher {year} no copy. Faltava aqui, e o
    // `react/prop-types` do airbnb quebrou o `yarn build` da main — o job de
    // PR só roda `yarn test`, que não passa pelo ESLint.
    year: PropTypes.number,
  }),
  awardsPath: PropTypes.string,
};
